import classNames from 'classnames';
import SVGIcon from '../../../components/atoms/icons';
import Text from '../../../components/atoms/text';
import {
	ContainerReportProps,
	MaintenanceReport,
	ShipStats,
} from '../../../services/Reports.service';
import { dateObjToStr } from '../../../utils/date';
import { useEffect } from 'react';
import Table, { TableColumn } from '../../../components/organisms/table';
import { useTranslation } from 'react-i18next';

const tableHeader: React.CSSProperties = {
	fontSize: '9px',
	border: '1px solid',
	padding: '4px',
};

const tableCell: React.CSSProperties = {
	fontSize: '9px',
	border: '1px solid',
	textAlign: 'center',
	padding: '4px',
	overflowWrap: 'anywhere',
};

const LowIncomeIcon = () => (
	<div className="bg-yellow p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-14 h-auto stroke-white" icon="Error" />
	</div>
);

const LowIncome = () => (
	<div className="flex flex-col items-center justify-center">
		<LowIncomeIcon />
		<Text className="w-min text-center text-sm">Baixo Rendimento</Text>
	</div>
);

const CriticalTemperatureIcon = () => (
	<div className="bg-red p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-10 h-auto stroke-white fill-white" icon="X" />
	</div>
);

const CriticalTemperature = () => (
	<div className="flex flex-col items-center justify-center">
		<CriticalTemperatureIcon />
		<Text className="w-min text-center text-sm">Temperatura Crítica</Text>
	</div>
);

const CriticalProblemIcon = () => (
	<div className="bg-black p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-10 h-auto" icon="Alert" />
	</div>
);

const CriticalProblem = () => (
	<div className="flex flex-col items-center justify-center">
		<CriticalProblemIcon />
		<Text className="w-min text-center text-sm">Problema Crítico</Text>
	</div>
);

const NoEnergyIcon = () => (
	<div className="bg-black border-[3px] border-black p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon
			className="w-10 h-auto"
			icon="NoEnergy"
		/>
	</div>
);

const NoEnergy = () => (
	<div className="flex flex-col items-center justify-center">
		<NoEnergyIcon />
		<Text className="w-min text-center text-sm">Sem Alimentação</Text>
	</div>
);

const SocketIcon = () => (
	<div className="bg-gray p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-10 h-auto fill-gray" icon="Socket" />
	</div>
);

const Socket = () => (
	<div className="flex flex-col items-center justify-center">
		<SocketIcon />
		<Text className="text-center text-sm w-16">Falta de Energia</Text>
	</div>
);

const CoolingdownIcon = () => (
	<div className="bg-white border-[3px] border-[#05C4F8] p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-10 h-auto fill-white" icon="Coolingdown" />
	</div>
);

const Coolingdown = () => (
	<div className="flex flex-col items-center justify-center">
		<CoolingdownIcon />
		<Text className="w-min text-center text-sm">Coolingdown</Text>
	</div>
);

const DefrostIcon = () => (
	<div className="bg-white border-[3px] border-[#D90706] p-3 rounded-xl size-16 justify-center items-center flex">
		<SVGIcon className="w-12 h-auto fill-white" icon="Defrost2" />
	</div>
);

const Defrost = () => (
	<div className="flex flex-col items-center justify-center">
		<DefrostIcon />
		<Text className="text-center text-sm w-16">Defrost</Text>
	</div>
);

interface MonitorLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
	data: ContainerReportProps[];
	isFinance?: boolean;
	shipStats?: ShipStats[];
	supplyReturnAtivado?: boolean;
}

const MonitorLayout = ({
	data,
	className,
	isFinance = false,
	shipStats = [],
	supplyReturnAtivado = false,
}: MonitorLayoutProps) => {
	const { t } = useTranslation();

	const renderPeriod = (date: Date) =>
		new Date(date).getHours() < 12 ? 'AM' : 'PM';

	const columns: TableColumn<ShipStats>[] = [
		{ key: 'ship', label: t('port_management.ship') },
		{ key: 'travelCode', label: t('movement.travel_code') },
		{
			key: 'containers',
			label: t('port_management.containers.title'),
			children: (row) => (
				<Text className="text-xs">{row.containers.length}</Text>
			),
		},
		{ key: 'monitoringTotal', label: t('monitor.total') },
		{
			key: 'price',
			label: t('misc.price'),
			children: (row) => (
				<Text className="text-xs">R$ {row.price.toFixed(2)}</Text>
			),
		},
	];

	const calcTotal = () => {
		const result = shipStats.reduce(
			(acc, ship) => acc + (ship.price || 0),
			0
		);
		return result.toFixed(2);
	};

	return (
		<div
			className={classNames(
				'print:mx-0 print:my-0 flex min-w-0 flex-col gap-5',
				className
			)}
		>
			<div className="flex flex-col items-center">
				<SVGIcon
					icon="InovaReefer"
					className="w-36 h-auto justify-self-center"
				/>
				<Text className="text-primary text-sm font-semibold text-center block">
					CNPJ: 57.456.341/0001-93
				</Text>
			</div>
			<Text as="h2" className="text-center block">
				Relatório por Container
			</Text>
			{isFinance && shipStats && (
				<>
					<div className="overflow-x-auto w-full">
						<Table
							data={shipStats}
							columns={columns}
							className={classNames(
								'grid-cols-[1fr_1fr_1fr_1fr_1fr]'
							)}
						/>
					</div>
					<Text as="h3" className="text-primary text-center">
						{t('misc.total_price')}: R$ {calcTotal()}
					</Text>
				</>
			)}
			<div className="flex flex-wrap justify-around gap-4">
				<LowIncome />
				<CriticalTemperature />
				<CriticalProblem />
				<NoEnergy />
				<Socket />
				<Coolingdown />
				<Defrost />
			</div>
			{data.map((container, index) => (
				<div key={index} className="min-w-0">
					<Text className="font-semibold">
						Container: {container.label}
						{container.carrier && ` - ${container.carrier}`}
					</Text>
					{/* Tabela de monitoramento simplificada */}
					<div className="w-full overflow-x-auto print:overflow-visible">
						<table
							className="min-w-[900px] print:min-w-0"
							style={{
								width: '100%',
								borderCollapse: 'collapse',
								marginTop: '20px',
							}}
						>
							<thead>
								<tr>
									<th style={tableHeader}>Data</th>
									<th style={tableHeader}>Período</th>
									<th style={tableHeader}>
										Entrada - Navio - Viagem
									</th>
									<th style={tableHeader}>
										Saída - Navio - Viagem
									</th>
									<th style={tableHeader}>SetPoint</th>
									{supplyReturnAtivado ? (
										<>
											<th style={tableHeader}>Supply</th>
											<th style={tableHeader}>Return</th>
										</>
									) : (
										<th style={tableHeader}>Temperatura</th>
									)}
									<th style={tableHeader}>Problemas</th>
									<th style={tableHeader}>Fotos</th>
									<th style={tableHeader}>Consig</th>
								</tr>
							</thead>
							<tbody>
								{container.monitors.map((item, i) => (
									<tr key={i} style={{ border: '1px solid' }}>
										<td style={tableCell}>
											{dateObjToStr(item.date)}
										</td>
										<td style={tableCell}>
											{renderPeriod(item.date)}
										</td>
										<td
											style={tableCell}
										>
											{dateObjToStr(item.date_in)} -{' '}
											{item.ship} - {item.travel}
										</td>
										<td
											style={tableCell}
										>
											{item.date_out && (
												<>
													{dateObjToStr(item.date_out)} -{' '}
													{item.ship} - {item.travel}
												</>
											)}
										</td>
										<td style={tableCell}>
											{item.errors?.includes(4) ||
											item.setpoint === 'offline' ||
											item.temperature === 'offline'
												? 'offline'
												: item.setpoint}
										</td>
										{(supplyReturnAtivado
											? [item.supply, item.return]
											: [item.temperature]
										).map((valor, indice) => (
											<td
												key={indice}
												style={tableCell}
											>
												{item.errors?.includes(4) ||
												item.setpoint === 'offline' ||
												item.temperature === 'offline'
													? 'offline'
													: valor ?? '-'}
											</td>
										))}
										<td style={tableCell}>
											<div className="flex flex-wrap justify-center gap-3 items-center [&>div]:shrink-0">
												{item.errors &&
													item.errors.map((error, indice) =>
														error === 1 || error === 12 ? (
															<LowIncomeIcon key={indice} />
														) : error === 13 ? (
															<CriticalTemperatureIcon key={indice} />
														) : error === 2 ? (
															<CoolingdownIcon key={indice} />
														) : error === 3 ? (
															<DefrostIcon key={indice} />
														) : error === 4 ? (
															<CriticalProblemIcon key={indice} />
														) : error === 6 ? (
															<NoEnergyIcon key={indice} />
														) : (
															<SocketIcon key={indice} />
														)
													)}
											</div>
										</td>
										<td style={tableCell}>
											<div className="flex flex-wrap justify-center gap-3 items-center">
												{item.images &&
													item.images.map(
														(src, imgIndex) => (
																<img
																	key={`${src}-${imgIndex}`}
																	src={src}
																alt={`Image ${index}-${i}-${imgIndex}`}
																	className="w-20 max-w-full h-auto shrink-0 object-cover rounded-md"
															/>
														)
													)}
											</div>
										</td>
										<td style={tableCell}>
											{item.owner}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<p className="text-center">
						Total de Monitoramentos: {container.monitors.length}
					</p>
				</div>
			))}

			<div className="mt-24 flex flex-col items-center text-center">
				<img
					className="place-self-center"
					src="/signature.png"
					alt="assinatura"
				/>
				<p>Francisco de Paula Luca</p>
				<p>CREA-AM 13.225D</p>
				<p>Engenheiro Proprietário</p>
				<p>
					Escritório: Rua Professor Guilherme Nery nº 480 - Parque 10
					de novembro - CEP 69.055-350 - Manaus/AM
				</p>
			</div>
		</div>
	);
};

export default MonitorLayout;
